import { useTranslation } from "providers/i18n"
import {
  Typography,
  CardMedia,
  CardContent,
  Box,
  Stack,
  useTheme,
  Card,
} from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import { LocationIcon } from "components/icons"
import { getFeatureDetail, IEventDetail, IPromotionDetail } from "utils/feature"
import { getDurationDate } from "utils/date"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { useMemo } from "react"
import { getIcon } from "utils/icon"
import { ImdfFeature } from "providers/venue/types"

interface IHighlightFeatureCardProps {
  feature: ImdfFeature
}

export const HighlightFeatureCard = ({
  feature,
}: IHighlightFeatureCardProps) => {
  const { t } = useTranslation()

  const { name, startDate, endDate, coverImage, location } = useMemo(
    () => getFeatureDetail(feature) as IPromotionDetail | IEventDetail,
    [feature]
  )

  const theme = useTheme()
  const { styleOverrides } = theme.components?.[`HighlightFeatureCard`] || {}
  const {
    root,
    cardMedia,
    cardContent,
    contentWrapper,
    cardTitle,
    cardInfoItem,
    iconWrapper,
    cardInfoWrapper,
  } = styleOverrides

  const durationText = useMemo(
    () => getDurationDate(startDate, endDate),
    [startDate, endDate]
  )

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarTodayIcon />,
    [theme]
  )

  return (
    <Card sx={root}>
      <CardMedia
        className="HighlightFeatureCard-cardMedia"
        sx={cardMedia}
        component="img"
        image={coverImage}
      />
      <CardContent sx={cardContent}>
        <Stack spacing={0} sx={contentWrapper}>
          <Typography
            sx={cardTitle}
            className="HighlightFeatureCard-cardTitle"
            variant="title"
            component="div"
          >
            {t(name)}
          </Typography>
          <Stack sx={cardInfoWrapper}>
            <Box sx={cardInfoItem}>
              {durationText && (
                <>
                  <Box sx={iconWrapper}>{calendarIcon}</Box>
                  <Typography variant="subtitle1" dir="ltr">
                    {durationText}
                  </Typography>
                </>
              )}
            </Box>
            {location && (
              <Box sx={cardInfoItem}>
                <Box sx={iconWrapper}>
                  <LocationIcon />
                </Box>
                <LocationInfo feature={feature} variant="subtitle1" />
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
